import { Component, OnInit, OnDestroy, ChangeDetectorRef, NgZone, ElementRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { forkJoin } from 'rxjs';
import _ from 'lodash';
import { Submission, Review } from 'src/app/model/paper';
import { Event } from 'src/app/model/event';
import { AdminService } from 'src/app/service/admin.service';
import { SubmissionsService } from 'src/app/service/submissions.service';
import { EventsService } from 'src/app/service/events.service';
import { ExportService } from 'src/app/service/export.service';
import { EXPORT_STYLES } from 'src/app/generated/export-styles.const';
import { MS_ICON_CSS, replaceMatIconsWithSvg, stripAngularDevAttrs } from 'src/app/utils/ms-icons';

/**
 * `ExportSubmissionsComponent` — v2 da página "Ver todas as submissões".
 *
 * Estratégia (out/2026, consolidada):
 *   1. Bulk fetch via `getSubmissionsByEventFull({detailed: true})` +
 *      `getFormReview` + `getFormSubmission` (mesmo trio do v1 legado).
 *   2. Hidrata `r.form` (cloneDeep) + distribui `r.answers` em
 *      `r.form.fields[i].answers`.
 *   3. Instancia TODOS os `<app-submission-export-view>` OFFSCREEN
 *      (`position:fixed; left:-100000px`) num único batch (`*ngFor`).
 *   4. Aguarda `NgZone.onStable` (Angular estabilizou tudo).
 *   5. Extrai `outerHTML` de cada wrapper → cacheia como SafeHtml.
 *   6. Preview via `[innerHTML]`; download embute `EXPORT_STYLES`.
 *
 * Trade-off (medido em evento pequeno, ~12 subs):
 *   - Tempo total ≈ mesmo do v1 legado (endpoint domina).
 *   - Wrapper Angular escala melhor que paper-info por sub (sem lazy loads,
 *     sem chair chrome interativo, sem event listeners).
 *
 * O antigo `ExportSubmissionsComponent` (rota `show-all-legacy`) foi
 * aposentado em out/2026 após validação de perf/paridade visual.
 */
@Component({
  selector: 'app-export-submissions',
  templateUrl: './export-submissions.component.html',
  styleUrls: ['./export-submissions.component.scss'],
})
export class ExportSubmissionsComponent implements OnInit, OnDestroy {
  public event: Event;
  public all: Submission[] = [];
  public expectedTotal = 0;
  public receivedCount = 0;
  public results = new Map<number, SafeHtml>();
  public batch: Submission[] = [];
  private metaMap = new Map<number, string>();
  private destroyed = false;
  private stylesEl: HTMLStyleElement | null = null;
  private perfStart = 0;

  constructor(
    private adminService: AdminService,
    private submissionsService: SubmissionsService,
    private eventsService: EventsService,
    private exportService: ExportService,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
    private zone: NgZone,
    private hostElRef: ElementRef,
    private activatedRoute: ActivatedRoute,
  ) {}

  /** O modo "site" (.zip) é tratado inline no ExportSubmissionsChooserComponent
   *  (com overlay, sem passar por esta rota). Aqui é sempre "single" (preview
   *  + download HTML único). */
  public downloadForMode(): void {
    this.downloadCombinedHtml();
  }

  ngOnInit(): void {
    this.perfStart = performance.now();
    console.log('[perf][v2] start');
    document.body.classList.add('export-submissions-active');
    // CSS pré-baked injetado na página v2 pro preview runtime dos fragmentos
    // ficar estilizado (a página v2 não instancia paper-info → paper-info.scss
    // não é carregado pelo Angular normalmente).
    if (!document.getElementById('export-styles-runtime')) {
      this.stylesEl = document.createElement('style');
      this.stylesEl.id = 'export-styles-runtime';
      // Neutraliza `min-height: calc(100vh - ...)` do `.paper-info` — pensado
       // pra visão de UMA submissão preencher viewport; no bulk (N empilhadas)
       // sheets menores ficam esticadas pra 725px, gerando gaps variáveis.
      this.stylesEl.textContent = EXPORT_STYLES + '\n' + MS_ICON_CSS +
        `\n.export-static-container .paper-info { min-height: 0 !important; }`;
      document.head.appendChild(this.stylesEl);
    }
    // Barra de trabalho GLOBAL do JEMS3 (topo). Start dentro do setTimeout
    // pra evitar ExpressionChangedAfterItHasBeenCheckedError — mudar o
    // `mode` do FixedTopProgressBarComponent no MESMO tick do ngOnInit do
    // pai bate no re-check do Angular. Adiar 1 tick resolve.
    setTimeout(() => {
      this.adminService.progressBar?.start();
      this.event = this.adminService.selectedEvent;
      if (this.event) { this.load(); }
      else { this.adminService.getEvent().subscribe(e => { this.event = e; this.load(); }); }
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    document.body.classList.remove('export-submissions-active');
    if (this.stylesEl) { this.stylesEl.remove(); this.stylesEl = null; }
    // Defensivo: se o usuário sair antes do render terminar, para a barra.
    this.adminService.progressBar?.stop();
  }

  /** Filtros vindos do chooser via query params. CSVs de valores; vazio = todos.
   *  Chaves: status, topics, tracks, paperGroup, tpcGroup — 1:1 com o filter-v2. */
  private filteredSubs(subs: Submission[]): Submission[] {
    const q = this.activatedRoute.snapshot.queryParamMap;
    const csv = (v: string | null) => (v || '').split(',').filter(Boolean);
    const statuses = csv(q.get('status'));
    const topics = csv(q.get('topics')).map(Number);
    const tracks = csv(q.get('tracks')).map(Number);
    const paperGroups = csv(q.get('paperGroup')).map(Number);
    const tpcGroups = csv(q.get('tpcGroup')).map(Number);
    return subs.filter(s => {
      if (statuses.length && !statuses.includes(s.status as any)) return false;
      if (tracks.length && !tracks.includes(+((s as any).track?.id ?? (s as any).track))) return false;
      if (paperGroups.length && !paperGroups.includes(+((s as any).paperGroup))) return false;
      if (tpcGroups.length && !tpcGroups.includes(+((s as any).tpcGroup))) return false;
      if (topics.length) {
        const st = ((s as any).topics || []).map((t: any) => +(t?.id ?? t));
        if (!st.some(id => topics.includes(id))) return false;
      }
      return true;
    });
  }

  private load(): void {
    forkJoin([
      this.submissionsService.getSubmissionsByEventFull(this.event.id, { detailed: true }),
      this.eventsService.getFormReview(this.event.id),
      this.eventsService.getFormSubmission(this.event.id),
    ]).subscribe(([rawSubs, reviewForm, submissionForm]: [Submission[], any, any]) => {
      const submissions = this.filteredSubs(rawSubs);
      submissions.forEach(sub => {
        (sub as any).event = this.event;
        if (submissionForm) { (sub as any).formSubmission = submissionForm; }
        (sub.reviews || []).forEach((r: any) => {
          if (r instanceof Review && reviewForm) {
            r.form = _.cloneDeep(reviewForm);
            r.form.fields = r.form.fields.map((f: any) => {
              f.answers = r.answers?.filter((a: any) => a?.formField === f.id) || [];
              return f;
            });
          }
        });
        this.all.push(sub);
        this.metaMap.set(sub.id, (sub.track as any)?.name ? `Trilha: ${(sub.track as any).name}` : '');
      });
      this.expectedTotal = submissions.length;
      this.receivedCount = submissions.length;
      this.cdr.detectChanges();
      this.zone.runOutsideAngular(() => this.renderBatch(submissions));
    });
  }

  private renderBatch(subs: Submission[]): void {
    this.zone.run(() => {
      this.batch = subs;
      this.cdr.detectChanges();
    });
    const finish = () => {
      const wrappers = this.hostElRef.nativeElement.querySelectorAll(
        '.worker-hidden > app-submission-export-view'
      ) as NodeListOf<HTMLElement>;
      wrappers.forEach((el, i) => {
        const sub = subs[i]; if (!sub) { return; }
        const pi = el.querySelector('#paperinfo') as HTMLElement | null;
        if (!pi) { return; }
        // Remove controles interativos (chevrons de expandir, botões de ação):
        // no show-all e no HTML baixado eles não fazem nada. Mesma regra do
        // paper-info individual (`[remove-from-export]:not(.status-stamp)`).
        const clone = pi.cloneNode(true) as HTMLElement;
        clone.querySelectorAll('[remove-from-export]:not(.status-stamp)').forEach(el => el.remove());
        // Substitui <mat-icon>...</mat-icon> Material Symbols por <span> +
        // classe CSS (mask-image data-URI) + limpa overhead do Angular dev
        // mode (ng-reflect, _ngcontent, bindings, ng-star-inserted) — ~63%
        // menor por página, ~1 MB no zip total pra ~200 subs.
        const html = stripAngularDevAttrs(replaceMatIconsWithSvg(clone.outerHTML));
        this.results.set(sub.id, this.sanitizer.bypassSecurityTrustHtml(html));
      });
      this.zone.run(() => {
        this.batch = [];
        this.cdr.detectChanges();
        requestAnimationFrame(() => this.measureAbstractLeads());
        this.adminService.progressBar?.stop();
        const elapsed = performance.now() - this.perfStart;
        console.log(`[perf][export] DONE ${subs.length} subs em ${(elapsed / 1000).toFixed(2)}s`);
      });
    };
    let done = false;
    const wrap = () => { if (done) { return; } done = true; sub.unsubscribe(); clearTimeout(safety); finish(); };
    const sub = this.zone.onStable.subscribe(wrap);
    const safety = setTimeout(wrap, 30000);
  }

  /** Mede `.abstract-lead` de cada `.abstract-block` no preview e seta
   *  `--abstract-lead-w` inline. Chamado após o [innerHTML] montar. */
  private measureAbstractLeads(): void {
    const blocks = this.hostElRef.nativeElement.querySelectorAll('.export-static-container .abstract-block');
    blocks.forEach((block: HTMLElement) => {
      const lead = block.querySelector('.abstract-lead') as HTMLElement | null;
      if (!lead) { return; }
      const w = lead.getBoundingClientRect().width;
      if (w) { block.style.setProperty('--abstract-lead-w', w + 'px'); }
    });
  }

  public metaOf(id: number): string { return this.metaMap.get(id) || ''; }
  public trackById(_i: number, s: Submission): number { return s.id; }

  get progressPercent(): number {
    if (this.expectedTotal === 0) return 0;
    return Math.round((this.results.size / this.expectedTotal) * 100);
  }
  get isDone(): boolean { return this.expectedTotal > 0 && this.results.size === this.expectedTotal; }
  get renderedCount(): number { return this.results.size; }

  private renderedCache: { size: number; html: SafeHtml } = { size: -1, html: '' };
  get renderedHtml(): SafeHtml {
    if (this.renderedCache.size === this.results.size) return this.renderedCache.html;
    const sorted = this.all.slice().sort((a, b) => a.id - b.id);
    const themeClass = (document.body.className.match(/\b(?:light|dark)-theme\b/) || ['light-theme'])[0] || 'light-theme';
    const inner = sorted.map(s => this.results.get(s.id)).filter(Boolean)
      .map(safe => (safe as any).changingThisBreaksApplicationSecurity as string).join('');
    // Wrap em `<app-paper-info>` STRING pros seletores globais de ancestral casarem.
    const html = `<app-paper-info class="${themeClass}">${inner}</app-paper-info>`;
    this.renderedCache = { size: this.results.size, html: this.sanitizer.bypassSecurityTrustHtml(html) };
    return this.renderedCache.html;
  }

  public downloadCombinedHtml(): void {
    if (!this.isDone) return;
    const themeClass = (document.body.className.match(/\b(?:light|dark)-theme\b/) || [''])[0];
    const sorted = this.all.slice().sort((a, b) => a.id - b.id);
    const fragments = sorted.map(s => this.results.get(s.id)).filter(Boolean)
      .map(safe => (safe as any).changingThisBreaksApplicationSecurity as string).join('');
    const eventAcr = this.event?.acronym || 'event';
    const filename = `${eventAcr}-submissions.html`;
    // Overscroll (macOS bounce, mobile stretch) revela o bg do `<html>`.
     // Sem esta regra html cai no default (branco). Aplica bg UNCONDICIONAL
     // pra light (dark tem override abaixo). O `:has()` anterior não bastava:
     // não é 100% suportado + `<html>` sem theme class no doc baixado.
    const isDark = themeClass === 'dark-theme';
    const bg = isDark ? '#000' : '#EEECEB';
    // `!important` porque _color-scheme.scss tem `html:has(body.light-theme)
    // {background:#fff}` com specificidade (0,1,1) que vence html plano (0,0,1).
    const canvasCss =
      `html{background-color:${bg} !important;}` +
      `body{background-color:${bg} !important;}` +
      // Font base do doc: sem isso o body cai no serif default do browser
      // (Times). O EXPORT_STYLES tem regras scoped em `.paper-info` mas o
      // `<body>` e wrappers externos precisam da fonte explicitamente.
      `body{font-family:'Geist',-apple-system,BlinkMacSystemFont,"Segoe UI","Roboto","Helvetica Neue",Helvetica,Arial,sans-serif;` +
      `font-size:0.875rem;line-height:1.25rem;letter-spacing:0.015625rem;` +
      `font-feature-settings:"kern","liga","calt","tnum";` +
      `-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;` +
      `text-rendering:optimizeLegibility;}`;
    // Script inline mede `.abstract-lead` e seta `--abstract-lead-w` no doc
    // baixado (mesmo motivo do preview). Roda no DOMContentLoaded + após
    // fonts.ready.
    const measureScript = `<script>(function(){function m(){document.querySelectorAll('.abstract-block').forEach(function(b){var l=b.querySelector('.abstract-lead');if(!l)return;var w=l.getBoundingClientRect().width;if(w)b.style.setProperty('--abstract-lead-w',w+'px');});}if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',m);else m();if(document.fonts&&document.fonts.ready)document.fonts.ready.then(m);else window.addEventListener('load',m);})();</script>`;
    const doc = `<!doctype html><html class="${themeClass}"><head><meta charset="utf-8">` +
      `<title>${eventAcr} — Submissões</title>` +
      `<link rel="preconnect" href="https://fonts.googleapis.com">` +
      `<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>` +
      `<link href="https://fonts.googleapis.com/css2?family=Geist:wght@100..900&family=Inconsolata:wght@200..900&family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200&display=swap" rel="stylesheet">` +
      `<style>${EXPORT_STYLES}\n${canvasCss}\n${MS_ICON_CSS}\n.paper-info { min-height: 0 !important; }</style>${measureScript}</head>` +
      `<body class="${themeClass}"><app-paper-info class="${themeClass || 'light-theme'}">${fragments}</app-paper-info></body></html>`;
    this.exportService.downloadFile(filename, doc, 'text/html');
  }
}
